<template>
  <q-card-section class="column col items-center justify-center gap-lg q-pa-xl">
    <div class="onboarding-icon-badge row items-center justify-center">
      <lucide-icon
        :name="view.icon"
        size="40px"
        :stroke-width="2"
        aria-hidden="true"
      />
    </div>

    <div class="column items-center gap-xxl text-center">
      <h1 class="text-28 text-font-semibold">{{ view.headline }}</h1>
      <div
        v-if="view.subline?.length"
        class="onboarding-subline column items-center gap-xs text-16 text-primary"
      >
        <p
          v-for="(line, index) in view.subline"
          :key="index"
          class="q-ma-none"
        >
          {{ line }}
        </p>
      </div>
    </div>
  </q-card-section>

  <q-card-actions
    class="column items-center gap-xs q-px-xl q-pt-none q-pb-md"
    align="center"
  >
    <q-btn
      v-if="view.secondaryCtaLabel"
      flat
      no-caps
      color="secondary"
      class="onboarding-action-btn sm-radius text-14"
      :loading="isRedirecting"
      :disable="isRedirecting"
      @click="onSwitchAccount"
    >
      {{ view.secondaryCtaLabel }}
    </q-btn>
    <q-btn
      no-caps
      unelevated
      color="brand"
      class="onboarding-action-btn sm-radius text-14 text-font-semibold"
      :disable="isRedirecting"
      @click="onContinue"
    >
      {{ view.ctaLabel }}
      <lucide-icon
        :name="view.ctaIcon"
        size="18px"
        color="white"
        class="q-ml-xs"
        aria-hidden="true"
      />
    </q-btn>
  </q-card-actions>
</template>

<script setup>
  import { computed, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  import { useAuthStore } from 'src/stores/auth'

  import { ROUTES } from 'src/router/const'
  import { notifyMsgs, notifyService } from 'src/services/app/notify.service'
  import { EMAIL_VERIFICATION_STATES, SESSION_PANEL_COPY } from 'src/services/consts/app.const'
  import { resolveEmailVerificationState } from 'src/services/helpers/auth.helper'

  // Authenticated visitor on /email-verified. Classifies the Auth0 result and
  // offers continue / switch — SessionGate keeps activePanel side-effect-free,
  // so this panel does not auto-redirect. Auth0's `message` query param is
  // never rendered — it only classifies the result inside the helper.

  const authStore = useAuthStore()
  const route = useRoute()
  const router = useRouter()

  const isRedirecting = ref(false)

  const view = computed(() => {
    const state = resolveEmailVerificationState({
      success: route.query.success,
      message: route.query.message,
    })
    const copy = SESSION_PANEL_COPY.emailVerifiedSession[state]
    const currentEmail = authStore.auth0User?.email || ''
    const shouldSwitchAccount =
      state === EMAIL_VERIFICATION_STATES.VERIFIED ||
      state === EMAIL_VERIFICATION_STATES.ALREADY_VERIFIED ||
      state === EMAIL_VERIFICATION_STATES.INVALID

    return {
      ...copy,
      secondaryCtaLabel: shouldSwitchAccount ? copy.secondaryCtaLabel : null,
      subline: copy.subline.map((line) => line.replace('{currentEmail}', currentEmail)),
    }
  })

  function onContinue() {
    router.push({ name: ROUTES.OVERVIEW })
  }

  async function onSwitchAccount() {
    isRedirecting.value = true
    try {
      // App entry, not /email-verified: verification is already done (or dead),
      // so Switch only needs logout → guard → Universal Login → back into /app.
      // Never round-trip Auth0 query params on returnTo.
      await authStore.logout({ returnTo: `/${ROUTES.APP}` })
    } catch {
      // authStore.logout already reports to PostHog — this is the user-facing half.
      isRedirecting.value = false
      notifyService.error(notifyMsgs.logoutError)
    }
  }
</script>
